import { useEffect, useMemo, useRef } from 'react';
import { useQuery } from '@tanstack/react-query';
import { api } from './api';
import { useAuth } from './auth';
/**
 * React-Query-backed trip sync.
 *
 * Uses refetchInterval for background polling and an accumulating `since` cursor
 * via a ref so each fetch only pulls deltas. Mutations should invalidate the
 * key ['sync', tripId] to force an immediate refetch.
 *
 * Pauses automatically when the tab is hidden (React Query behavior).
 */
export function useTripSync(tripId, intervalMs = 5000) {
    const { user } = useAuth();
    const sinceRef = useRef(undefined);
    const mergedRef = useRef(null);
    useEffect(() => {
        // Reset when trip changes
        sinceRef.current = undefined;
        mergedRef.current = null;
    }, [tripId]);
    const q = useQuery({
        queryKey: ['sync', tripId],
        enabled: !!tripId,
        refetchInterval: intervalMs,
        refetchIntervalInBackground: false,
        queryFn: async () => {
            const qs = sinceRef.current ? `?since=${encodeURIComponent(sinceRef.current)}` : '';
            const delta = await api.get(`/trips/${tripId}/sync${qs}`);
            const merged = mergeSnapshot(mergedRef.current, delta);
            mergedRef.current = merged;
            sinceRef.current = delta.now;
            return merged;
        },
    });
    const enriched = useMemo(() => (q.data ? enrichSnapshot(q.data, user?.id) : null), [q.data, user?.id]);
    return { data: enriched, error: q.error ?? null, isLoading: q.isLoading };
}
function enrichSnapshot(snap, userId) {
    const voteByPool = new Map();
    for (const v of snap.votes) {
        let m = voteByPool.get(v.pool_item_id);
        if (!m) {
            m = new Map();
            voteByPool.set(v.pool_item_id, m);
        }
        m.set(v.user_id, v.type);
    }
    const commentCountByPool = new Map();
    for (const c of snap.comments) {
        if (c.target_type !== 'pool_item')
            continue;
        commentCountByPool.set(c.target_id, (commentCountByPool.get(c.target_id) ?? 0) + 1);
    }
    const notDeleted = (x) => !x.deleted_at;
    const pool = snap.pool.filter(notDeleted).map((p) => {
        const vm = voteByPool.get(p.id) ?? new Map();
        return {
            ...p,
            tags: Array.isArray(p.tags) ? p.tags : [],
            votes: Object.fromEntries(vm),
            my_vote: userId ? (vm.get(userId) ?? null) : null,
            comment_count: commentCountByPool.get(p.id) ?? 0,
        };
    });
    return {
        ...snap,
        pool,
        itinerary: snap.itinerary.filter(notDeleted),
        comments: snap.comments.filter(notDeleted),
        expenses: snap.expenses.filter(notDeleted),
    };
}
function mergeSnapshot(prev, delta) {
    if (!prev)
        return delta;
    return {
        now: delta.now,
        trip: delta.trip ?? prev.trip,
        members: delta.members.length ? delta.members : prev.members,
        pool: mergeBy(prev.pool, delta.pool, 'id'),
        votes: mergeVotes(prev.votes, delta.votes),
        itinerary: mergeBy(prev.itinerary, delta.itinerary, 'id'),
        comments: mergeBy(prev.comments, delta.comments, 'id'),
        expenses: mergeBy(prev.expenses, delta.expenses, 'id'),
        expense_shares: delta.expense_shares.length ? delta.expense_shares : prev.expense_shares,
        activities: mergeBy(prev.activities, delta.activities, 'id').slice(0, 100),
    };
}
function mergeBy(prev, incoming, key) {
    if (!incoming.length)
        return prev;
    const map = new Map(prev.map((x) => [x[key], x]));
    for (const x of incoming)
        map.set(x[key], x);
    return Array.from(map.values());
}
function mergeVotes(prev, incoming) {
    if (!incoming.length)
        return prev;
    const map = new Map(prev.map((v) => [`${v.pool_item_id}:${v.user_id}`, v]));
    for (const v of incoming)
        map.set(`${v.pool_item_id}:${v.user_id}`, v);
    return Array.from(map.values());
}
